import React, { useState, useEffect, useRef } from 'react';
import { 
  Shield, ShieldCheck, Briefcase, FileText, MessageSquare, Settings, Search, Bell, User, Lock, LogOut, MoreVertical, 
  Download, Upload, Plus, CheckCircle2, Clock, Eye, Layers, X, ChevronRight, ChevronLeft, 
  FilePlus2, ArrowUp, ArrowDown, CheckSquare, CalendarDays, ArrowRight, Edit, Trash2, Save, Calendar, 
  MapPin, Gavel, Users, Receipt, Banknote, RefreshCw, Mail, Sliders, ArrowDownAZ, Image as ImageIcon, FileEdit, 
  MoveUp, MoveDown, RotateCw, Settings2, Database, UserCog, Scan, Printer, MonitorDown, Copy, FolderSync, Menu, Paperclip, ExternalLink, Folder, FolderOpen, UploadCloud
} from 'lucide-react';
import { ROLE_NAMES, formatHebDate, handleDownloadLocalFile } from '../utils';

export default function UserManagementView({ firmUsers, dbOps, currentUserProfile }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'lawyer' });

  const hasManagementPermission = () => {
    return currentUserProfile?.role === 'admin' || currentUserProfile?.role === 'secretary';
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!hasManagementPermission()) {
      alert("אין לך הרשאה לבצע פעולה זו.");
      return;
    }
    const exists = firmUsers.find(u => u.email.toLowerCase() === newUser.email.trim().toLowerCase());
    if (exists) { alert("משתמש עם כתובת דוא\"ל זו כבר קיים במערכת."); return; }
    dbOps.handleSave('firmUsers', { name: newUser.name, email: newUser.email.trim().toLowerCase(), role: newUser.role });
    setIsModalOpen(false); setNewUser({ name: '', email: '', role: 'lawyer' });
  };

  const handleEditUser = (e) => {
    e.preventDefault();
    if (!hasManagementPermission()) {
      alert("אין לך הרשאה לבצע פעולה זו.");
      return;
    }
    dbOps.handleSave('firmUsers', { name: editingUser.name, email: editingUser.email.trim().toLowerCase(), role: editingUser.role }, editingUser._id);
    setEditingUser(null);
  };

  const handleDeleteUser = (userToDelete) => {
    if (!hasManagementPermission()) {
      alert("אין לך הרשאה לבצע פעולה זו.");
      return;
    }
    if (userToDelete._id === currentUserProfile._id || userToDelete.email === currentUserProfile.email) { alert("אינך יכול להסיר את המשתמש של עצמך."); return; }
    dbOps.handleDelete('firmUsers', userToDelete._id); 
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-full animate-in fade-in">
      <div className="p-4 md:p-6 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between bg-slate-50/50 gap-4">
        <div>
          <h3 className="font-bold text-lg text-slate-800">ניהול והרשאות משתמשים</h3>
          <p className="text-sm text-slate-500 mt-1">הוסף והסר אנשי צוות, ונהל את הרשאות הגישה שלהם למערכת.</p>
        </div>
        <button onClick={() => setIsModalOpen(true)} className="w-full sm:w-auto shrink-0 bg-blue-700 hover:bg-blue-800 text-white px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors shadow-sm">
          <Plus className="w-4 h-4" /> משתמש חדש
        </button>
      </div>
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-right whitespace-nowrap min-w-[500px]">
          <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 text-sm">
            <tr>
              <th className="p-4 font-medium">שם משתמש</th><th className="p-4 font-medium">דוא"ל להתחברות</th><th className="p-4 font-medium">תפקיד / הרשאה</th><th className="p-4 font-medium">פעולות</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-sm">
            {firmUsers.map((u) => {
              const isMe = u._id === currentUserProfile._id || u.email === currentUserProfile.email;
              return (
                <tr key={u._id} className="hover:bg-blue-50/30 transition-colors">
                  <td className="p-4 font-semibold text-slate-800 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-xs shrink-0">{u.name.substring(0, 1)}</div>
                    {u.name} {isMe && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full mr-2">אני</span>}
                  </td>
                  <td className="p-4 text-slate-600" dir="ltr">{u.email}</td>
                  <td className="p-4">
                    <span className={`px-2.5 py-1 rounded text-xs font-medium border ${u.role === 'admin' ? 'bg-purple-50 text-purple-700 border-purple-200' : u.role === 'secretary' ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                      {ROLE_NAMES[u.role] || u.role}
                    </span>
                  </td>
                  <td className="p-4 flex gap-2">
                    <button onClick={() => setEditingUser(u)} className="text-slate-400 hover:text-blue-600 transition-colors p-1" title="ערוך משתמש"><Edit className="w-5 h-5" /></button>
                    {!isMe && <button onClick={() => handleDeleteUser(u)} className="text-slate-400 hover:text-red-600 transition-colors p-1" title="הסר משתמש"><Trash2 className="w-5 h-5" /></button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {isModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 m-0" dir="rtl">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><UserCog className="w-5 h-5 text-blue-600" /> הוספת משתמש חדש</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleAddUser} className="p-5 space-y-4">
              <div><label className="block text-xs font-medium text-slate-700 mb-1">שם מלא</label><input required type="text" placeholder="למשל: דוד כץ" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={newUser.name} onChange={e => setNewUser({...newUser, name: e.target.value})} /></div>
              <div><label className="block text-xs font-medium text-slate-700 mb-1">דוא"ל (ישמש להתחברות דרך SSO)</label><input required type="email" placeholder="email@lawfirm.co.il" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={newUser.email} onChange={e => setNewUser({...newUser, email: e.target.value})} /></div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">תפקיד / הרשאה</label>
                <select className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={newUser.role} onChange={e => setNewUser({...newUser, role: e.target.value})}>
                  <option value="lawyer">עו"ד (ללא הרשאת ניהול משתמשים)</option>
                  <option value="intern">מתמחה (הרשאות כשל עו"ד)</option>
                  <option value="secretary">מזכירות (כולל הרשאת ניהול משתמשים)</option>
                  <option value="admin">מנהלה / עו"ד ראשי (הרשאות מלאות)</option>
                </select>
              </div>
              <div className="pt-4 flex justify-end gap-3 mt-2 border-t border-slate-100">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">ביטול</button>
                <button type="submit" className="px-4 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"><Save className="w-4 h-4"/> שמור משתמש</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {editingUser && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 m-0" dir="rtl">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-slate-800 flex items-center gap-2"><UserCog className="w-5 h-5 text-blue-600" /> עריכת פרטי משתמש</h3>
              <button onClick={() => setEditingUser(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleEditUser} className="p-5 space-y-4">
              <div><label className="block text-xs font-medium text-slate-700 mb-1">שם מלא</label><input required type="text" placeholder="שם המשתמש..." className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={editingUser.name} onChange={e => setEditingUser({...editingUser, name: e.target.value})} /></div>
              <div><label className="block text-xs font-medium text-slate-700 mb-1">דוא"ל (ישמש להתחברות דרך SSO)</label><input required type="email" placeholder="email@lawfirm.co.il" className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={editingUser.email} onChange={e => setEditingUser({...editingUser, email: e.target.value})} /></div>
              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">תפקיד / הרשאה</label>
                <select className="w-full p-2.5 border border-slate-300 rounded-lg text-sm outline-none focus:border-blue-500" value={editingUser.role} onChange={e => setEditingUser({...editingUser, role: e.target.value})}>
                  <option value="lawyer">עו"ד (ללא הרשאת ניהול משתמשים)</option>
                  <option value="intern">מתמחה (הרשאות כשל עו"ד)</option>
                  <option value="secretary">מזכירות (כולל הרשאת ניהול משתמשים)</option>
                  <option value="admin">מנהלה / עו"ד ראשי (הרשאות מלאות)</option>
                </select>
              </div>
              <div className="pt-4 flex justify-end gap-3 mt-2 border-t border-slate-100">
                <button type="button" onClick={() => setEditingUser(null)} className="px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">ביטול</button>
                <button type="submit" className="px-4 py-2.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2"><Save className="w-4 h-4"/> שמור שינויים</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
